import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Loader2, History, FileText, BrainCircuit, Edit3, CheckCircle2, Lock } from "lucide-react";
import { getRecordHistory } from "./api";

const ICONS = {
  imported: FileText,
  ai_assessed: BrainCircuit,
  manual_edit: Edit3,
  manual_allocation: Edit3,
  recalculated: BrainCircuit,
  approved: CheckCircle2,
  committed: Lock,
};

const COLOURS = {
  imported: "text-slate-400",
  ai_assessed: "text-cyan-400",
  manual_edit: "text-orange-400",
  manual_allocation: "text-amber-400",
  recalculated: "text-purple-400",
  approved: "text-emerald-400",
  committed: "text-purple-300",
};

/**
 * @param {{ open: boolean, recordId: string|null, onClose: () => void }} props
 */
export default function HistoryDrawer({ open, recordId, onClose }) {
  const [history, setHistory] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open && recordId) {
      setLoading(true);
      getRecordHistory(recordId)
        .then((res) => setHistory(res.data.history || []))
        .catch(() => setHistory([]))
        .finally(() => setLoading(false));
    }
  }, [open, recordId]);

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="bg-[#0B1120] border-slate-800 max-w-md max-h-[85vh] overflow-y-auto" data-testid="history-drawer">
        <DialogHeader>
          <DialogTitle className="font-barlow text-slate-50 flex items-center gap-2">
            <History className="w-5 h-5 text-cyan-400" /> Record History
          </DialogTitle>
          <DialogDescription className="text-slate-500 text-xs">
            Imported → AI assessed → User corrected → Committed
          </DialogDescription>
        </DialogHeader>
        {loading && <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 text-cyan-400 animate-spin" /></div>}
        {!loading && history && history.length > 0 && (
          <div className="space-y-3" data-testid="history-list">
            {history.map((h, i) => {
              const Icon = ICONS[h.type] || FileText;
              const colour = COLOURS[h.type] || "text-slate-400";
              return (
                <div key={i} className="flex items-start gap-3 text-xs" data-testid={`history-entry-${i}`}>
                  <div className={`w-7 h-7 rounded-md bg-slate-800/60 flex items-center justify-center shrink-0 ${colour}`}>
                    <Icon className="w-3.5 h-3.5" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2">
                      <span className={`font-semibold ${colour}`}>{h.type.replace(/_/g, " ")}</span>
                      <span className="text-[10px] text-slate-600 font-mono">{new Date(h.created_at).toLocaleString("en-GB")}</span>
                    </div>
                    {h.data && (
                      <pre className="mt-1 text-[10px] text-slate-500 font-mono overflow-x-auto whitespace-pre-wrap break-words">
                        {JSON.stringify(h.data, null, 2)}
                      </pre>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
        {!loading && history && history.length === 0 && (
          <div className="text-center py-8 text-xs text-slate-500">No history recorded yet.</div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="border-slate-700" data-testid="history-close-btn">Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
